import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { SERVICES } from "@/lib/constants";

export const Services = ({ variant = "full" }) => {
  return (
    <section
      data-testid="services-section"
      className="bg-white relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-6">
            <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
              02 — Clinical Services Dashboard
            </div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-[#0A2540]">
              Four pillars of{" "}
              <span className="italic text-[#2E5AA5]">diagnostic care</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 self-end">
            <p className="font-sans text-base text-[#475569] leading-relaxed">
              An integrated dashboard of specialised services — from routine
              pathology to gold-standard FNAC — every report reviewed
              personally by our Chief Pathologist.
            </p>
          </div>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-[#E2E8F0]"
          data-testid="services-grid"
        >
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.slug}
              data-testid={`service-card-${s.slug}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.6,
                delay: i * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="group relative bg-white border-r border-b border-[#E2E8F0] p-8 lg:p-12 min-h-[340px] flex flex-col hover:bg-[#F8FAFC] transition-colors duration-500"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-serif text-3xl text-[#B08A4A]">
                  {s.number}
                </span>
                <ArrowUpRight
                  strokeWidth={1.2}
                  size={20}
                  className="text-[#94A3B8] group-hover:text-[#0A2540] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300"
                />
              </div>
              <h3 className="font-serif text-2xl lg:text-3xl text-[#0A2540] leading-tight mb-3">
                {s.title}
              </h3>
              <p className="font-sans text-sm text-[#475569] leading-relaxed mb-6">
                {variant === "full" ? s.long : s.short}
              </p>
              <ul className="mt-auto space-y-2">
                {s.highlights.map((h) => (
                  <li
                    key={h}
                    className="flex items-start gap-2 font-sans text-xs text-[#0A2540]"
                  >
                    <Check
                      size={12}
                      strokeWidth={2}
                      className="mt-0.5 text-[#B08A4A] shrink-0"
                    />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
              {s.path && (
                <Link
                  to={s.path}
                  data-testid={`service-card-link-${s.slug}`}
                  className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] hover:text-[#0A2540] transition-colors"
                >
                  Explore dedicated page
                  <ArrowUpRight size={13} strokeWidth={1.5} />
                </Link>
              )}
            </motion.article>
          ))}
        </div>

        {variant === "preview" && (
          <div className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-t border-[#E2E8F0] pt-10">
            <p className="font-serif text-2xl md:text-3xl italic text-[#0A2540] max-w-xl leading-snug">
              Every service, reviewed personally by a{" "}
              <span className="text-[#B08A4A]">Gold Medalist</span>.
            </p>
            <Link
              to="/services"
              data-testid="services-explore-link"
              className="btn-secondary"
            >
              Full Service Directory
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;
